import { Button } from 'antd';
import { useTranslate } from '@tolgee/react';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useSendTiktokLogisticsCard } from 'views/Conversation/Sider/useLogisticsListBusiness';
import { UILogistics } from './utils';
import { useToggle } from 'ahooks';
import { useMemo } from 'react';
import { ScreenshotModal } from 'views/Conversation/components/SendLogisticsInfo/ScreenshotModal';

interface BaseToolbarProps {
  conversation?: Conversation.Conversation;
  data: UILogistics;
  sendable: boolean;
}

type ViewType = 'original' | 'translate';

interface ToolbarProps extends BaseToolbarProps {
  viewType: ViewType;
  onToggleViewType: (type: ViewType) => void;
  onTriggerTranslate: () => void;
}

const SendButton = (props: BaseToolbarProps) => {
  const { conversation, data, sendable } = props;
  const { t } = useTranslate();
  const sendTiktokLogisticsCard = useSendTiktokLogisticsCard();
  const { externalTradeId, packageNumber } = data;

  const canSend =
    sendable &&
    !!packageNumber &&
    !!externalTradeId &&
    !!conversation &&
    isTiKTokUpperCase(conversation.channel);

  if (!canSend) {
    return null;
  }

  return (
    <Button
      size="small"
      type="primary"
      className="!text-xs"
      onClick={() => {
        sendTiktokLogisticsCard({
          orderId: externalTradeId,
          packageId: packageNumber,
          conversation,
        });
      }}
    >
      {t('translation.Send')}
    </Button>
  );
};

const ScreenshotButton = (props: BaseToolbarProps) => {
  const { conversation, data } = props;

  const { t } = useTranslate();
  const [visible, { toggle: toggleVisible }] = useToggle();

  const screenshotPackages = useMemo(() => {
    return [
      {
        trackingNumber: data.trackingNumber,
        shippingCarrier: data.shippingCarrier,
        firstMileTrackingNumber: data.firstMileTrackingNumber,
        trackingInfo: data.trackingInfo.map((tracking) => ({
          updateTime: tracking.updateTime,
          description: tracking.description,
        })),
      },
    ];
  }, [data]);

  if (!conversation) {
    return null;
  }

  return (
    <>
      <Button
        size="small"
        className="!text-xs !bg-[#ECECED] !text-[#515359] !border-none hover:opacity-80 !font-medium"
        onClick={toggleVisible}
      >
        {t('btn_generate_screenshot')}
      </Button>
      {visible && (
        <ScreenshotModal
          visible={visible}
          platform={conversation.channel}
          region={conversation.region}
          packages={screenshotPackages}
          onClose={toggleVisible}
        />
      )}
    </>
  );
};

export const Toolbar = (props: ToolbarProps) => {
  const { viewType, onToggleViewType, onTriggerTranslate, ...otherProps } = props;
  const { t } = useTranslate();

  const isOriginal = viewType === 'original';

  return (
    <div className="flex items-center gap-3">
      <SendButton {...otherProps} />
      <ScreenshotButton {...otherProps} />
      {!isOriginal && (
        <Button
          size="small"
          className="!text-xs !bg-[#ECECED] !text-[#515359] !border-none hover:opacity-80 !font-medium"
          onClick={onTriggerTranslate}
        >
          {t('translation.Translate')}
        </Button>
      )}
      <Button
        size="small"
        className="!text-xs !bg-[#ECECED] !text-[#515359] !border-none hover:opacity-80 !font-medium"
        onClick={() => onToggleViewType(viewType === 'original' ? 'translate' : 'original')}
      >
        {viewType === 'original' ? t('trade.查看翻译') : t('trade.查看原文')}
      </Button>
    </div>
  );
};
